// PostHog wrapper. Single-import surface so the rest of the app doesn't
// know which analytics library we use, and so we no-op cleanly when no
// VITE_POSTHOG_KEY is configured (local dev, preview deploys without
// the env var, etc.).
//
// Convention mirrors dashboard-stack:
//   VITE_POSTHOG_KEY   — project API key (phc_…). Optional. If unset,
//                         every helper here is a no-op.
//   VITE_POSTHOG_HOST  — instance host. Defaults to https://us.i.posthog.com.
//
// Super-property `app: 'fleet-triage'` is set on every event so the
// project can multiplex across sibling apps (dashboard-stack uses the
// same key) and dashboards can filter cleanly.

import posthog from 'posthog-js';

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!apiKey) return; // No key → silently disabled. No errors, no warnings.
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
  posthog.init(apiKey, {
    api_host: apiHost,
    // capture_pageview: history-based — we use react-router so SPAs need
    // the SDK's history-listener variant. Default is 'true' which only
    // captures the first load.
    capture_pageview: 'history_change',
    // Auto-capture clicks + form submits — high-cardinality but the
    // operator population is small (<20) so volume is manageable.
    autocapture: true,
    // Default session recording off — we'll opt in later if needed.
    disable_session_recording: true,
    // Identify on every page so refresh-after-Auth0 callback still
    // attributes correctly even before our useEffect runs.
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      ph.register({ app: 'fleet-triage' });
    },
  });
  initialized = true;
}

// Returns the singleton client or null when analytics are disabled.
// Callers can check truthiness before invoking advanced features
// (groups, identify, capture with sensitive metadata, etc.).
export function getAnalytics() {
  if (!initialized) return null;
  return posthog;
}

// Identify the operator after Auth0 authenticates. Idempotent — PostHog
// dedupes if called with the same distinct_id repeatedly.
export function identifyOperator(user: {
  sub?: string;
  email?: string;
  name?: string;
  [key: string]: unknown;
}): void {
  if (!initialized || !user?.sub) return;
  posthog.identify(user.sub, {
    email: user.email,
    name: user.name,
  });
}

// Clear identity on logout so the next operator on the same browser
// starts a fresh PostHog session and isn't attributed to the previous one.
export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}

// --- Custom events ---
//
// Track a domain event. No-ops when PostHog isn't configured (local dev,
// preview deploys without VITE_POSTHOG_KEY). Property bag is shallow-stringly-
// typed — keep keys snake_case, values JSON-serializable (number, string,
// boolean, null). PII-sensitive values go through identifyOperator already;
// don't pass email/name on capture.
//
// Event catalog (see ClickUp doc "PostHog Custom Events"):
//   - issue_confirmed   : operator pressed Confirm on /triage
//   - class_changed     : ticket_class_id changed via action bar
//   - task_created      : work-task row created (post-confirm or post-action)
//   - undo_clicked      : operator clicked Undo on a triage toast
//
// All events carry the `app: 'fleet-triage'` super-property automatically
// (registered in initAnalytics).
export type AnalyticsEvent =
  | 'issue_confirmed'
  | 'class_changed'
  | 'task_created'
  | 'undo_clicked';

export function trackEvent(
  name: AnalyticsEvent,
  props: Record<string, string | number | boolean | null | undefined> = {},
): void {
  if (!initialized) return;
  // Strip undefined so PostHog property panels stay clean.
  const clean: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined) clean[k] = v;
  }
  posthog.capture(name, clean);
}
